import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'zh' | 'fr';

interface Translations {
  [key: string]: {
    [key in Language]: string;
  };
}

export const translations: Translations = {
  home: { en: 'Home', zh: '首页', fr: 'Accueil' },
  lab: { en: 'Lab', zh: '实验室', fr: 'Labo' },
  clinic: { en: 'Clinic', zh: '诊所', fr: 'Clinique' },
  myOrders: { en: 'My Orders', zh: '我的订单', fr: 'Mes Commandes' },
  paymentSetup: { en: 'Payment Setup', zh: '支付设置', fr: 'Paiement' },
  admin: { en: 'Admin', zh: '管理', fr: 'Admin' },
  printing: { en: '3D Printing', zh: '3D打印', fr: 'Impression 3D' },
  contact: { en: 'Contact', zh: '联系我们', fr: 'Contact' },
  signIn: { en: 'Sign In', zh: '登录', fr: 'Connexion' },
  logout: { en: 'Logout', zh: '退出', fr: 'Déconnexion' },
  cart: { en: 'Cart', zh: '购物车', fr: 'Panier' },
  shippingDestination: { en: 'Shipping Destination', zh: '收货目的地', fr: 'Destination d\'expédition' },
  recipientName: { en: 'Recipient Name', zh: '收件人姓名', fr: 'Nom du destinataire' },
  phoneNumber: { en: 'Phone Number', zh: '电话号码', fr: 'Numéro de téléphone' },
  arrivalTime: { en: 'Preferred Arrival Time', zh: '首选送达时间', fr: 'Heure d\'arrivée préférée' },
  finalizeTransaction: { en: 'Finalize Transaction', zh: '完成交易', fr: 'Finaliser la transaction' },
  morning: { en: 'Morning (8AM-12PM)', zh: '上午 (8AM-12PM)', fr: 'Matin (8h-12h)' },
  afternoon: { en: 'Afternoon (1PM-5PM)', zh: '下午 (1PM-5PM)', fr: 'Après-midi (13h-17h)' },
  anytime: { en: 'Anytime', zh: '任何时间', fr: 'N\'importe quand' },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(() => {
    return (localStorage.getItem('piggy_dental_lang') as Language) || 'en';
  });

  useEffect(() => {
    localStorage.setItem('piggy_dental_lang', language);
  }, [language]);

  const t = (key: string) => {
    return translations[key]?.[language] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};
